/* eslint-disable import/no-duplicates */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { format, parseISO } from 'date-fns';

import { Container, Button, Row, Col } from 'react-bootstrap';

import { FaCheckCircle } from 'react-icons/fa';

import api from '../../../services/api';

import {
  formatPrice,
  TIPO_USUARIO,
  ORIGEM_PAGAMENTO,
} from '../../../utils/funcoes';

import { useToast } from '../../../hooks/toast';
import { useClinica } from '../../../hooks/clinica';
import { useAuth } from '../../../hooks/auth';
import { useCategoria } from '../../../hooks/categoria';
import { useFormaPagamento } from '../../../hooks/formapagamento';

import Input from '../../../components/Input';
import Select from '../../../components/Select';
import SelectSync, { IOption } from '../../../components/SelectSync';
import Loading from '../../../components/Loading';
import Modal from '../../../components/Modal';
import Pagamento from '../../../components/Pagamento';
import Paginacao from '../../../components/Paginacao';

import getValidationErrors from '../../../utils/getValidationErrors';

import { RelatorioHeader } from './styles';
import getResponseErrors from '../../../utils/getResponseErrors';
import HeaderSimples from '../components/HeaderSimples';

interface IPagamento {
  descricao: string;
  valor: number;
  valorFormatado: string;
}

interface IRelatorio {
  CprCodigo: number;
  CprCodOrigem: number;
  Data: Date;
  DataFormatado: string;
  CprHistorico: string;
  CprValor: number;
  CprValorPag: number;
  CprValorPagFormatado: string;
  CprClinica: number;
  CprPago: boolean;
  CatNome: string;
  pagamento: IPagamento[];
}

interface IRetornoRelatorio {
  data: IRelatorio[];
  count: number;
}

interface ITotais {
  totalPagoFormatado: string;
  totalPagarFormatado: string;
}

interface IFormBusca {
  dataInicio: Date;
  dataFim: Date;
  CliCodigo: string;
  CatCodigo: string;
  CprHistorico: string;
  CprFormaPag: string;
}

interface IPaginacao extends IFormBusca {
  limite: number;
  pagina: number;
}

interface IDataExibe {
  dataHora: string;
  dataDiaSemana: string;
}

interface IDadosPagamento {
  tituloPagamento: string;
  pagamentoSelecionado?: number;
  codOrigem?: number;
}

const Despesas: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { user } = useAuth();
  const { optionsClinica } = useClinica();

  const { optionCategorias, buscaCategoria } = useCategoria();
  const { optionsFormasPagamento, buscaFormaPagamento } = useFormaPagamento();

  const formRefBusca = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [dadosRelatorio, setDadosRelatorio] = useState<IRelatorio[]>();
  const [totais, setTotais] = useState<ITotais>();
  const [paginacao, setPaginacao] = useState<IPaginacao>();
  const [totalRegistros, setTotalRegistros] = useState<number>();
  const initialData = {
    dataInicio: format(new Date(), 'yyyy-MM-dd'),
    dataFim: format(new Date(), 'yyyy-MM-dd'),
    CliCodigo: user.UsuClinica.toString(),
  };
  const [dataExibe, setDataExibe] = useState<IDataExibe>();
  const [clinica, setClinica] = useState('');
  const [showModalPagamento, setShowModalPagamento] = useState(false);
  const [dadosPagamento, setDadosPagamento] = useState<
    IDadosPagamento | undefined
  >();

  const [optionsCategoria, setOptionsCategoria] = useState<IOption[]>();

  const exibeTotais = user.UsuTipo === TIPO_USUARIO.ADMINISTRADOR;

  useEffect(() => {
    if (user.UsuTipo > TIPO_USUARIO.SUPERVISOR) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuTipo]);

  useEffect(() => {
    async function loadPerfis(): Promise<void> {
      await buscaCategoria();
    }
    if (!optionCategorias || Object.keys(optionCategorias).length === 0)
      loadPerfis();
    setOptionsCategoria(optionCategorias.despesa);
  }, [buscaCategoria, optionCategorias]);

  useEffect(() => {
    async function loadFormaPagto(): Promise<void> {
      await buscaFormaPagamento();
    }
    if (
      !optionsFormasPagamento.optionFormaPagamento ||
      Object.keys(optionsFormasPagamento.optionFormaPagamento).length === 0
    )
      loadFormaPagto();
  }, [buscaFormaPagamento, optionsFormasPagamento]);

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        if (!paginacao) return;
        const {
          dataFim,
          dataInicio,
          CliCodigo,
          CatCodigo,
          CprFormaPag,
          CprHistorico,
          limite,
          pagina,
        } = paginacao;

        const dataInicioSelecionada = parseISO(dataInicio.toString());
        const dataFimSelecionada = parseISO(dataFim.toString());

        const dataInicioFormatada = format(dataInicioSelecionada, 'yyyy-MM-dd');
        const dataFimFormatada = format(dataFimSelecionada, 'yyyy-MM-dd');
        let filtrosTexto = '';
        let parametrosOpcionais = '';
        if (CatCodigo) {
          parametrosOpcionais += `&CatCodigo=${CatCodigo}`;
          filtrosTexto += ` - Categoria: ${
            optionsCategoria?.find(opt => opt.value === CatCodigo)?.label
          }`;
        }
        if (CprFormaPag) {
          parametrosOpcionais += `&CprFormaPag=${CprFormaPag}`;
          filtrosTexto += ` - Pagamento: ${
            optionsFormasPagamento.optionFormaPagamento?.find(
              opt => opt.value === CprFormaPag,
            )?.label
          }`;
        }
        if (CprHistorico) {
          parametrosOpcionais += `&CprHistorico=${CprHistorico}`;
          filtrosTexto += ` - Descrição: ${CprHistorico}`;
        }

        setDataExibe({
          dataDiaSemana: `${format(
            dataInicioSelecionada,
            'dd/MM/yyyy',
          )} até ${format(dataFimSelecionada, 'dd/MM/yyyy')} ${filtrosTexto}`,
          dataHora: format(new Date(), 'dd/MM/yyyy HH:mm'),
        });

        const response = await api.get<IRetornoRelatorio>(
          `/relatorios/despesas?dataInicio=${dataInicioFormatada}&dataFim=${dataFimFormatada}&CliCodigo=${CliCodigo}${parametrosOpcionais}&limite=${limite}&pagina=${pagina}`,
        );

        const { data, count } = response.data;

        let totalPago = 0;
        let totalPagar = 0;

        const dadosFormatados = data.map(rel => {
          const DataFormatado = format(
            parseISO(rel.Data.toString()),
            'dd/MM/yyyy',
          );

          rel.CprPago
            ? (totalPago += rel.CprValorPag)
            : (totalPagar += rel.CprValorPag);

          return {
            ...rel,
            DataFormatado,
            CprValorPagFormatado: formatPrice(rel.CprValorPag),
            pagamento: rel.pagamento
              ? rel.pagamento.map(des => {
                  return { ...des, valorFormatado: formatPrice(des.valor) };
                })
              : [{ descricao: '', valor: 0, valorFormatado: 'R$ 0,00' }],
          };
        });

        setDadosRelatorio(dadosFormatados);
        setTotalRegistros(count);
        setTotais({
          totalPagoFormatado: formatPrice(totalPago),
          totalPagarFormatado: formatPrice(totalPagar),
        });
      } catch (error) {
        const mensagem = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Não foi possível carregar dados',
          description: mensagem,
        });
      } finally {
        setLoading(false);
      }
    }

    if (paginacao) loadDados();
  }, [
    paginacao,
    addToast,
    optionsCategoria,
    optionsFormasPagamento.optionFormaPagamento,
  ]);

  const handleSubmitBusca = useCallback(
    async (dados: IFormBusca) => {
      setLoading(true);
      try {
        formRefBusca.current?.setErrors({});
        const schema = Yup.object().shape({
          dataInicio: Yup.date().required(),
          dataFim: Yup.date().required(),
          CliCodigo: Yup.string().required(),
          CatCodigo: Yup.string().nullable(true),
          CprFormaPag: Yup.string().nullable(true),
          CprHistorico: Yup.string().nullable(true),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        setClinica(
          optionsClinica.find(cli => cli.value === dados.CliCodigo)?.label ||
            '',
        );

        setPaginacao({
          ...dados,
          pagina: 1,
          limite: 300,
        });
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefBusca.current?.setErrors(errors);
        } else {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível carregar dados',
            description: mensagem,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast, optionsClinica],
  );

  const handlePagamento = useCallback(
    (titulo: string, cprCodigo?: number, cprCodOrigem?: number) => {
      setDadosPagamento({
        tituloPagamento: titulo,
        pagamentoSelecionado: cprCodigo,
        codOrigem: cprCodOrigem,
      });

      setShowModalPagamento(true);
    },
    [],
  );

  const handleFecharModalPagamento = useCallback(async (load: boolean) => {
    setShowModalPagamento(false);
    setDadosPagamento(undefined);
    if (load) {
      formRefBusca.current?.submitForm();
      setDadosPagamento(undefined);
    }
  }, []);

  const handleProximaPagina = useCallback((page: number) => {
    setPaginacao(state => {
      return state && { ...state, pagina: page };
    });
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => {}, []);

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}
      {showModalPagamento && (
        <Modal
          show={showModalPagamento}
          titulo={dadosPagamento?.tituloPagamento || ''}
          handleFecharModal={() => setShowModalPagamento(false)}
        >
          <Pagamento
            handleFechar={handleFecharModalPagamento}
            tipo={ORIGEM_PAGAMENTO.DESPESA.toString()}
            cprCodigo={dadosPagamento?.pagamentoSelecionado}
          />
        </Modal>
      )}
      <>
        <div className="d-print-none bg-white border p-2 mb-2">
          <div className="p-2 mb-2 d-flex justify-content-between">
            <h2 id="topoBusca">Despesas</h2>
            <Button
              type="button"
              variant="outline-secondary"
              size="sm"
              onClick={() =>
                handlePagamento(
                  'Inserir Despesa',
                  ORIGEM_PAGAMENTO.DESPESA,
                  undefined,
                  // eslint-disable-next-line prettier/prettier
                )}
            >
              Inserir Despesa
            </Button>
          </div>

          <Form
            ref={formRefBusca}
            initialData={initialData}
            onSubmit={handleSubmitBusca}
          >
            <Row noGutters>
              <Col md={2} className="pr-1">
                <Input name="dataInicio" label="De" type="date" required />
              </Col>
              <Col md={2} className="pr-1">
                <Input name="dataFim" label="Até" type="date" required />
              </Col>
              <Col md={2} className="pr-1">
                <SelectSync
                  name="CatCodigo"
                  label="Categoria"
                  options={optionsCategoria || []}
                  isClearable
                  handleSelectChange={handleOnChangeVazio}
                />
              </Col>
              <Col md={2} className="pr-1">
                <SelectSync
                  name="CprFormaPag"
                  label="Forma de Pagamento"
                  options={optionsFormasPagamento.optionFormaPagamento}
                  isClearable
                  handleSelectChange={handleOnChangeVazio}
                />
              </Col>
              <Col md={2} className="pr-1">
                <Input name="CprHistorico" label="Histórico" type="text" />
              </Col>
              <Col md={1} className="pr-1">
                <Select
                  name="CliCodigo"
                  label="Clinica"
                  options={optionsClinica}
                  disabled={user.UsuTipo !== TIPO_USUARIO.ADMINISTRADOR}
                />
              </Col>
              <Col md={1}>
                <br />
                <Button type="submit" size="sm" variant="outline-secondary">
                  BUSCAR
                </Button>
              </Col>
            </Row>
          </Form>
        </div>

        <div className="bg-white border p-2 my-3">
          <HeaderSimples dataImpressao={dataExibe?.dataHora} />
        </div>

        <RelatorioHeader>
          DESPESAS {`- ${clinica} -`} {dataExibe?.dataDiaSemana}
        </RelatorioHeader>
        <div className="bg-white border p-2" style={{ minHeight: '300px' }}>
          {dadosRelatorio && (
            <>
              <b>Total de {totalRegistros} registros</b>
              <table
                className="table table-hover table-sm table-font-small table-bordered"
                style={{ borderWidth: '0px' }}
              >
                <thead>
                  <tr className="text-center">
                    <th style={{ width: '85px' }}>Data</th>
                    <th style={{ minWidth: '160px' }}>Categoria</th>
                    <th style={{ width: '100%' }}>Descrição</th>
                    <th style={{ minWidth: '135px' }}>Pagamento</th>
                    <th style={{ minWidth: '120px' }}>Valor</th>
                    <th style={{ width: '35px' }}>-</th>
                  </tr>
                </thead>
                <tbody>
                  {dadosRelatorio &&
                    dadosRelatorio.map(rel => (
                      <tr
                        key={`despesas-${rel.CprCodigo}`}
                        data-id={rel.CprCodigo}
                        style={{ fontSize: '.7rem', height: '30px' }}
                      >
                        <td>{rel.DataFormatado}</td>
                        <td>{rel.CatNome}</td>
                        <td>{rel.CprHistorico}</td>
                        <td>
                          {rel.pagamento.map(pag => (
                            <span
                              key={`pagD-${rel.CprCodigo}-${pag.descricao}`}
                              className="d-block"
                            >
                              {pag.descricao}
                            </span>
                          ))}
                        </td>
                        <td>
                          {rel.CprPago ? (
                            rel.pagamento.map(pag => (
                              <span
                                key={`pagV-${rel.CprCodigo}-${pag.descricao}`}
                                className="d-block"
                              >
                                {pag.valorFormatado}
                              </span>
                            ))
                          ) : (
                            <span
                              key={`naoPago-${rel.CprCodigo}-${rel.CprHistorico}`}
                              className="d-block"
                            >
                              {rel.CprValorPagFormatado}
                            </span>
                          )}
                        </td>

                        <td>
                          <Button
                            data-id={rel.CprCodigo}
                            type="button"
                            size="sm"
                            style={{ padding: '1px 8px' }}
                            className={
                              rel.CprPago ? 'bg-success' : 'bg-secondary'
                            }
                            onClick={
                              () =>
                                handlePagamento(
                                  `Pagar Parcela - ${rel.CprHistorico}`,
                                  rel.CprCodigo,
                                  rel.CprCodOrigem,
                                )
                              // eslint-disable-next-line react/jsx-curly-newline
                            }
                          >
                            <FaCheckCircle size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  {exibeTotais && (
                    <tr
                      key="fdfdsfsdfdf"
                      style={{
                        fontSize: 'Small',
                        height: '30px',
                        fontWeight: 'bold',
                      }}
                    >
                      <td> </td>
                      <td colSpan={2} style={{ textAlign: 'right' }}>
                        Total Pago: {totais?.totalPagoFormatado}
                      </td>
                      <td colSpan={3} style={{ textAlign: 'right' }}>
                        Total a Pagar: {totais?.totalPagarFormatado}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          )}
          <Paginacao
            limitePagina={paginacao?.limite || 300}
            paginasVizinhas={2}
            totalRegistros={totalRegistros}
            paginaInicio={paginacao?.pagina}
            handleProximaPagina={handleProximaPagina}
          />
        </div>
      </>
    </Container>
  );
};

export default Despesas;
